import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, GraduationCap, CheckCircle2 } from 'lucide-react';
export function PortalsSection() {
  const [activeTab, setActiveTab] = useState('leerling');
  const tabs = [{
    id: 'leerling',
    label: 'Voor Leerlingen',
    icon: <User className="w-5 h-5" data-id="element-565" />
  }, {
    id: 'ouder',
    label: 'Voor Ouders',
    icon: <Users className="w-5 h-5" data-id="element-566" />
  }, {
    id: 'leraar',
    label: 'Voor Leraren',
    icon: <GraduationCap className="w-5 h-5" data-id="element-567" />
  }];
  const content = {
    leerling: {
      title: 'Leren voelt als een spel',
      description: 'Een eigen, veilige omgeving waar je kind zelfstandig aan de slag kan. Geen afleiding, gewoon lekker oefenen.',
      features: ['Verdien XP en speel leuke badges vrij', 'Vrolijke animaties bij elk goed antwoord', 'Hulp en uitleg als het even niet lukt', 'Zelf kiezen wat je wilt oefenen'],
      color: 'text-orange-500',
      bg: 'bg-orange-500',
      light: 'bg-orange-50'
    },
    ouder: {
      title: 'Altijd op de hoogte',
      description: 'Weet precies hoe je kind ervoor staat, zonder zelf sommen na te hoeven kijken. Wij doen het werk.',
      features: ['Wekelijkse update in je mailbox', 'Inzicht in waar je kind moeite mee heeft', 'Concrete tips om thuis te helpen', 'Meerdere kinderen in één overzicht'],
      color: 'text-emerald-500',
      bg: 'bg-emerald-500',
      light: 'bg-emerald-50'
    },
    leraar: {
      title: 'De hele klas in beeld',
      description: 'Data-gedreven inzichten om gerichte instructie te geven waar het echt nodig is. Bespaar tijd op nakijken.',
      features: ['Direct zien wie extra uitleg nodig heeft', 'Voortgang per vak en per leerling', 'Huiswerk klaarzetten met één klik', 'Volledig afgestemd op de Cito-doelen'],
      color: 'text-blue-500',
      bg: 'bg-blue-500',
      light: 'bg-blue-50'
    }
  };
  const activeContent = content[activeTab as keyof typeof content];
  return <section className="py-20 lg:py-32 bg-[#FFF9F2] border-y border-orange-100" data-id="element-568">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-569">
        <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-570">
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight" data-id="element-571">
            Eén platform,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-572">
              drie verhalen
            </span>
          </h2>
          <p className="text-lg text-slate-600 font-medium" data-id="element-573">
            Je kind ziet een spel. Jij ziet een rapportje. De juf ziet de klas.
            Iedereen krijgt precies wat hij nodig heeft — niet meer, niet
            minder.
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-16" data-id="element-574">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          const tabContent = content[tab.id as keyof typeof content];
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center gap-2 px-6 py-3 rounded-2xl text-base font-bold transition-all ${isActive ? 'text-white shadow-md' : 'text-slate-600 hover:bg-white hover:shadow-sm'}`} data-id="element-575">
                {isActive && <motion.div layoutId="activePortalTab" className={`absolute inset-0 rounded-2xl ${tabContent.bg} -z-10`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-576" />}
                <span className="relative z-10 flex items-center gap-2" data-id="element-577">
                  {tab.icon}
                  {tab.label}
                </span>
              </button>;
        })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[320px]" data-id="element-578">
          <AnimatePresence mode="wait" data-id="element-579">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="max-w-4xl mx-auto bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-slate-100" data-id="element-580">
              <div className="text-center mb-10" data-id="element-581">
                <h3 className="text-3xl font-black text-slate-900 mb-4" data-id="element-582">
                  {activeContent.title}
                </h3>
                <p className="text-lg text-slate-600 font-medium max-w-2xl mx-auto" data-id="element-583">
                  {activeContent.description}
                </p>
              </div>
              <ul className="grid sm:grid-cols-2 gap-6 text-left" data-id="element-584">
                {activeContent.features.map((feature, idx) => <li key={idx} className={`flex items-start gap-4 ${activeContent.light} p-5 rounded-2xl`} data-id="element-585">
                    <CheckCircle2 className={`w-6 h-6 ${activeContent.color} shrink-0`} data-id="element-586" />
                    <span className="text-slate-800 font-bold" data-id="element-587">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}